'use client';

import React, { useEffect, useMemo } from 'react';
import { Box } from '@mantine/core';
import { SectionTitle } from '@/shared/SectionTitle';
import { RootBox } from '@/shared/RootBox';
import { useAppDispatch } from '@/hooks/reduxHooks';
import { SelectedPage } from '@/types/types';
import { IndexesContentType } from '@/types/contentTypes';
import { useInView } from 'react-intersection-observer';
import { setSelectedPage } from '@/features/menuLinks/menuLinksSlice';
import { useMediaQuery } from '@mantine/hooks';
import { LineChartData } from '@/types/chartTypes';
/* import { useParallax } from 'react-scroll-parallax'; */
import { SectionSubtitle } from '@/shared/SectionSubtitle';
import dynamic from 'next/dynamic';
import { ResponsiveLineChartComponent } from './ResponsiveLineChartComponent';

/* const ElementMeshWhite = dynamic(() => import('@/../public/abstractShapes/elementMeshWhite.svg'), { ssr: false }); */

/* const ResponsiveLineChartComponent = dynamic(
  () =>
    import('./ResponsiveLineChartComponent').then(
      (mod) => mod.ResponsiveLineChartComponent
    ),
  { ssr: false }
); */

const IndexesDescription = dynamic(
  () => import('./IndexesDescription').then((mod) => mod.IndexesDescription),
  { ssr: false }
);
const ChartLegend = dynamic(
  () => import('./ChartLegend').then((mod) => mod.ChartLegend),
  { ssr: false }
);

const EssensesAndApplication = dynamic(
  () =>
    import('./EssensesAndApplication').then(
      (mod) => mod.EssensesAndApplication
    ),
  { ssr: false }
);

const EssensesAndApplicationMobile = dynamic(
  () =>
    import('./EssensesAndApplicationMobile').then(
      (mod) => mod.EssensesAndApplicationMobile
    ),
  { ssr: false }
);

type Props = {
  chartData: LineChartData;
  content: IndexesContentType;
};

export const SystematicPreselection = ({ chartData, content }: Props) => {
  const dispatch = useAppDispatch();
  const isOnLargeScreen = useMediaQuery('(min-width: 1060px)');

  /*   const { ref: parallaxRef } = useParallax<HTMLDivElement>({
    speed: 15,
    scale: [0.4, 1],
    rotate: [-30, 0],
  }); */

  const { ref, inView } = useInView({
    threshold: 0.2,
  });

  useEffect(() => {
    if (inView) {
      dispatch(setSelectedPage(SelectedPage.SystematicPreselection));
    }
  }, [inView, dispatch]);

  const desktopTickValues = useMemo(
    () => [
      '1h2018',
      '2h2018',
      '1h2019',
      '2h2019',
      '1h2020',
      '2h2020',
      '1h2021',
      '2h2021',
      '1h2022',
      '2h2022',
      '1h2023',
      '2h2023',
      '1h2024',
      '2h2024',
      '1h2025',
    ],
    []
  );

  const mobileTickValues = useMemo(
    () => ['1h2018', '1h2021', '1h2024', '1h2025'],
    []
  );

  const memoizedChartData = useMemo(() => chartData, [chartData]);

  const memoizedContent = useMemo(
    () => ({
      differences: content.differences,
      applications: content.applications,
      essenses: content.essenses,
    }),
    [content]
  );

  return (
    <Box
      className="relative  h-full w-full"
      id="performancecomparison"
      ref={ref}
    >
      <RootBox>
        {/*   {isOnLargeScreen ? <div
          ref={parallaxRef}
          className='absolute h-60 w-60 rotate-45 opacity-30 right-40'
        >
          <ElementMeshWhite />
        </div> : <div ref={parallaxRef} style={{ position: 'absolute' }}></div>} */}
        <SectionTitle title="Performance Comparison" index={5} />
        <SectionSubtitle subtitle="Arcanis Capital Systematic Strategy vs. S&P 500 vs. Stableton Unicorn 20 Reference Index" />
        <p className="mt-6 text-lg text-secondary-500 dark:text-lightBlue-200/80">
          Tracking Portfolio Growth Across Key Indexes and Our Proprietary Strategy
        </p>
        {/* <SectionSubtitle
          mt={6}
          subtitle='The indexes demonstrate interesting benchmark for VC investor returns which is provably achievable.'
          highlights={['interesting benchmark', 'achievable']}
        /> */}

        {/* <h3 className="mt-12 font-montserrat text-xl font-bold uppercase tracking-wider text-primary-500">
          DIFFERENCE
        </h3>
        <div className="mt-6">
          <IndexesDescription content={memoizedContent.differences} />
        </div>

        <h3 className="mt-12 font-montserrat text-xl font-bold uppercase tracking-wider text-primary-500">
          EXAMPLE
        </h3> */}
        <Box className="mt-6 w-full text-lightBlue-500">
          <Box className="flex h-auto w-full overflow-visible rounded-xl bg-secondary-200/10 text-xl dark:bg-lightBlue-500/10">
            <Box className="relative flex h-[70vh] w-full flex-col justify-center overflow-visible p-8 xxs:max-md:h-96 xxs:max-md:p-2">
              <p className="mr-4 font-semibold text-secondary-400 xxs:max-md:ml-4 xxs:max-md:mt-4 xxs:max-md:text-base dark:text-primary-500">
                1M Investment performance comparison (AC REV GROWTH TOP5 vs Stableton Unicorn 20 vs SnP), 2018-2024
              </p>
              {isOnLargeScreen ? null : (
                <p className="ml-4 mt-2 text-sm font-semibold text-secondary-400 dark:text-lightBlue-500">
                  Return (x, Cash-on-Cash)
                </p>
              )}
              <ResponsiveLineChartComponent
                chartData={memoizedChartData}
                tickValues={
                  isOnLargeScreen ? desktopTickValues : mobileTickValues
                }
                isOnLargeScreen={isOnLargeScreen}
              />
            </Box>
          </Box>
          {isOnLargeScreen ? (
            <Box className="relative mt-4 flex">
              <ChartLegend />
            </Box>
          ) : null}
          {/*  {isOnLargeScreen ? (
            <Box className="mt-12 flex h-full w-full flex-col text-2xl">
              <EssensesAndApplication
                essensesContent={memoizedContent.essenses}
                applicationsContent={memoizedContent.applications}
              />
            </Box>
          ) : (
            <EssensesAndApplicationMobile
              essensesContent={memoizedContent.essenses}
              applicationsContent={memoizedContent.applications}
            />
          )} */}
        </Box>
      </RootBox>
    </Box>
  );
};

/* const areEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.chartData === nextProps.chartData &&
    prevProps.content === nextProps.content
  );
};

Indexes.displayName = 'Indexes';

export default React.memo(Indexes, areEqual); */
